import { perfilMarca } from '@aosenuma/lib-types';
import { Logo } from './Logo';

interface TopbarProps {
  /** Texto fuerte a la izquierda del divider, e.g. "Proyecto arrendamiento de tierras". */
  title: string;
  /** Chip de usuario a la derecha (modo legacy: solo etiqueta + badge MFA). */
  userChip?: { label: string; mfa?: boolean };
  /**
   * Sustituye el chip por un nodo custom (e.g. UserMenuDropdown). Si está
   * presente, el `userChip` se ignora. El nodo debe encargarse de su propio
   * estilo de chip — convencionalmente reusa la clase `aosenuma-topbar__user-chip`
   * para mantener look-and-feel.
   */
  userChipContent?: React.ReactNode;
  /** Slot extra a la derecha (e.g. switch de modo Tablero/Fullmap). */
  rightSlot?: React.ReactNode;
}

/**
 * Topbar - replica el visor original `/huimilpan_elawan/visor/`.
 *
 * - Altura compacta 44 px (desktop) / auto-wrap en mobile.
 * - Fondo navy translucido con backdrop-filter.
 * - Linea decorativa inferior teal -> lime -> gold a 30% de opacidad.
 *
 * Mobile (≤640px): el bloque de título se contrae a solo el logo Elawan.
 * El bloque derecho hace scroll horizontal sin desbordar el header.
 * Los estilos viven en globals.css `.aosenuma-topbar*` para que las media
 * queries puedan override sin pelear con inline styles.
 */
export function Topbar({ title, userChip, userChipContent, rightSlot }: TopbarProps) {
  const marca = perfilMarca();
  return (
    <header className="aosenuma-topbar">
      {/* línea decorativa inferior (teal → lime → gold) */}
      <span aria-hidden className="aosenuma-topbar__line" />

      <div className="aosenuma-topbar__title-block">
        {marca.neutral ? (
          /* Sin logo de cliente: una palabra sobria ocupa el mismo hueco. */
          <span className="aosenuma-topbar__wordmark">aosenuma report</span>
        ) : (
          <Logo
            variant="elawan-color"
            height={22}
            className="aosenuma-topbar__logo-elawan"
          />
        )}
        <span className="aosenuma-topbar__divider" />
        <span className="aosenuma-topbar__title-text">
          <strong className="aosenuma-topbar__title-strong">{title}</strong>
          {/* En neutro el producto ya es aosenuma; el crédito sobraría. */}
          {!marca.ocultarPoweredBy && (
            <>
              <span className="aosenuma-topbar__title-sep">·</span>
              <span className="aosenuma-topbar__powered">powered by</span>
              <Logo
                variant="aosenuma-icon-white"
                height={13}
                className="aosenuma-topbar__logo-aosenuma"
              />
              <span className="aosenuma-topbar__brand">aosenuma</span>
            </>
          )}
        </span>
      </div>

      <div className="aosenuma-topbar__right">
        {userChipContent
          ? userChipContent
          : userChip && (
            <span className="aosenuma-topbar__user-chip">
              {userChip.mfa && (
                <span className="aosenuma-topbar__user-mfa">MFA</span>
              )}
              <span className="aosenuma-topbar__user-label">{userChip.label}</span>
            </span>
          )}
        {rightSlot}
      </div>
    </header>
  );
}
